<template>
  <div class="fullHeight">
    <div class="legenda">
      <div v-for="stop in stops" :key="stop[0]">
        <span
          :style="{
            backgroundColor: stop[1],
            width: '0.7rem',
            height: '0.7rem',
            borderRadius: '50%',
            border: '1px solid grey',
            display: 'inline-block',
          }"
        ></span>
        <span class="">
          {{ stop[0] }}
        </span>
      </div>
    </div>

    <div class="posicao-right">
      <div>
        <md-button class="md-icon-button md-raised" @click="centerMap">
          <md-icon>my_location</md-icon>
        </md-button>
      </div>
    </div>

    <div id="mapContainer" class="fullHeight" />
  </div>
</template>

<script>
import 'mapbox-gl/dist/mapbox-gl.css'

import turf from '@turf/turf'
import mapboxgl from 'mapbox-gl' // or "const mapboxgl = require('mapbox-gl');"

import { EventBus, Topics, Types } from '@/event-bus'
import http from '@/utils/http'
import { STOPS_PRESSAO } from '@/utils/mapa-escala-pressao'
import {
  registerChannelListener,
  removeChannelListener,
} from '@/utils/web-socket'

import environment from '../../environment'

export default {
  name: 'EpanetHeatmap',
  props: {
    localId: {
      type: String,
      required: true,
    },
    styleMap: {
      type: String,
      default: 'mapbox://styles/mapbox/streets-v12',
    },
    pontos: {
      type: Boolean,
      default: true,
    },
    threeD: {
      type: Boolean,
      default: false,
    },
    region: {
      type: Boolean,
      default: true,
    },
    lines: {
      type: Boolean,
      default: false,
    },
    reservatorios: {
      type: Boolean,
      default: true,
    },
    geojsonRef: {
      type: Object,
      default: null,
    },
    point: {
      type: Object,
      default: null,
    },
    tempPointLine: {
      type: Object,
      default: null,
    },
    realTime: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      icones: {
        reversatorio: require('@/assets/map/v1/hd/reservatorio.png'),
      },
      map: null,
      environment,
      geojson: null,
      linesGeoJson: null,
      reservatoriosGeoJson: null,
      epanetHubRef: null,
      horarioCalibracao: null,
      marker: null,
      searchMarker: null,
      stops: STOPS_PRESSAO,
      equipamentosGeoJson: null,
      stopsLine: [
        [60, 'rgba(126, 186, 181, 1)'],
        [100, 'rgba(96, 153, 59, 1)'],
        [150, 'rgba(54, 103, 53, 1)'],
        [200, 'rgba(17, 66, 50, 1)'],
        [250, 'rgba(50, 89, 40, 1)'],
        [300, 'rgba(39, 53, 42, 1)'],
        [350, 'rgba(55, 66, 47, 1)'],
        [400, 'rgba(0, 131, 81, 1)'],
        [450, 'rgba(44, 50, 34, 1)'],
        [500, 'rgba(54, 52, 42, 1)'],
        [600, 'rgba(107, 124, 89, 1)'],
      ],
    }
  },
  watch: {
    realTime: {
      immediate: true,
      handler(newValue) {
        if (newValue === true) {
          if (!this.epanetHubRef) {
            this.connectEpanet()
          }
        } else {
          if (this.epanetHubRef) {
            removeChannelListener(
              'epanet:' + this.localId,
              'epanet:update',
              this.epanetHubRef
            )
          }
        }
      },
    },
    styleMap: {
      immediate: true,
      handler(newValue) {
        if (this.map) {
          this.map.setStyle(newValue)
        }
      },
    },
    pontos: {
      immediate: true,
      handler(newValue) {
        if (newValue === true) {
          this.addPontos()
        } else {
          if (this.map) {
            this.map.removeLayer('sensores-point')
            this.map.removeSource('sensores')
          }
        }
      },
    },
    tempPointLine: {
      immediate: true,
      handler(newValue) {
        this.addTempLine()
      },
    },
    lines: {
      immediate: true,
      handler(newValue) {
        if (newValue === true) {
          this.addLines()
        } else {
          if (this.map) {
            this.map.removeLayer('lines')
            this.map.removeSource('lines')
          }
        }
      },
    },
    threeD: {
      immediate: true,
      handler(newValue) {
        if (newValue === true) {
          this.add3D()
        } else {
          if (this.map) {
            this.map.removeLayer('3d')
            this.map.removeSource('polygon')
          }
        }
      },
    },
    reservatorios: {
      immediate: true,
      handler(newValue) {
        if (newValue === true) {
          this.addReservatorios()
        } else {
          if (this.map) {
            this.map.removeLayer('reservatorios')
            this.map.removeSource('reservatorios')
          }
        }
      },
    },
    region: {
      immediate: true,
      handler(newValue) {
        if (newValue === true) {
          this.addRegions()
        } else {
          if (this.map) {
            this.map.getStyle().layers.forEach((layer) => {
              if (layer.id.startsWith('region')) {
                this.map.removeLayer(layer.id)
              }
            })
          }
        }
      },
    },
    geojsonRef: {
      immediate: true,
      handler(newValue) {
        if (newValue !== null) {
          this.renderPipeline(newValue)
        }
      },
    },
    point: {
      immediate: true,
      handler(newValue) {
        this.addPoint()
      },
    },
  },
  beforeDestroy() {
    if (this.epanetHubRef) {
      removeChannelListener(
        'epanet:' + this.localId,
        'epanet:update',
        this.epanetHubRef
      )
    }
  },
  async mounted() {
    if (this.realTime === false && this.geojsonRef !== null) {
      this.geojson = this.geojsonRef
    }

    this.loadMap()
  },
  methods: {
    processGeoJson(geoJson) {
      const lines = []
      const points = []
      const reservatorios = []

      geoJson.features.forEach((e) => {
        switch (e.properties.group) {
          case 'LINE':
            lines.push(e)
            break
          case 'PCP':
            points.push(e)
            if (e.properties.nodetype === '1') {
              reservatorios.push(
                turf.point(
                  [e.geometry.coordinates[0], e.geometry.coordinates[1]],
                  {
                    ...e.properties,
                  }
                )
              )
            }
            break
          default:
        }
      })

      this.linesGeoJson = {
        ...geoJson,
        features: lines,
      }

      this.reservatoriosGeoJson = {
        ...geoJson,
        features: reservatorios,
      }

      this.geojson = {
        ...geoJson,
        features: points,
      }

      this.loadEquipamentos()
    },
    flyTo(e) {
      if (this.map === null) {
        return
      }

      this.map.flyTo({
        center: e.coordinates,
        zoom: e.zoom || 18,
      })
    },
    highlightPoint({ coordinates }) {
      if (this.map == null) return
      if (this.searchMarker != null) {
        this.searchMarker.remove()
        this.searchMarker = null
      }
      const el = document.createElement('div')
      el.className = 'search-highlight-marker'
      this.searchMarker = new mapboxgl.Marker({ element: el })
        .setLngLat(coordinates)
        .addTo(this.map)
      setTimeout(() => {
        if (this.searchMarker) {
          this.searchMarker.remove()
          this.searchMarker = null
        }
      }, 3500)
    },
    flyToCenter(pointsCoordinates) {
      // Comment to save performance
      const points = turf.points(pointsCoordinates)

      const medianCenter = turf.centerMedian(points)

      const center = medianCenter.geometry.coordinates

      this.map.flyTo({
        center: center,
        zoom: 13,
        bearing: 0,
        pitch: 0,
      })
    },
    renderPipeline(geoJson) {
      const oldValue = this.geojson
      this.processGeoJson(geoJson)

      this.addPoint()
      this.addPontos()
      this.add3D()
      this.addLines()
      this.addRegions()
      this.addReservatorios()
      this.addEquipamentos()

      if (oldValue == null) {
        this.flyToCenter(
          this.geojson.features.map((e) => e.geometry.coordinates)
        )
      }
    },
    centerMap() {
      if (this.geojson == null || this.geojson.features.length === 0) {
        return
      }

      this.flyToCenter(this.geojson.features.map((e) => e.geometry.coordinates))
    },
    connectEpanet() {
      registerChannelListener(
        'epanet:' + this.localId,
        'epanet:update',
        (payload) => {
          if (this.map == null) {
            return
          }

          if (this.realTime === false) {
            return
          }

          this.renderPipeline(payload.data)

          this.$emit('calibrado')
        }
      ).then((e) => {
        EventBus.$emit(Topics.NOTIFICATIONS, {
          type: Types.INFO,
          message: 'Conectado ao Epanet',
        })
        this.epanetHubRef = e
      })
    },
    addRegions() {
      if (this.geojson == null) {
        return
      }

      if (this.map == null) {
        return
      }

      if (this.region === false) {
        return
      }

      const pointsByRegion = this.geojson.features.reduce((acc, e) => {
        const point = turf.point(e.geometry.coordinates)

        if (
          e.properties.pattern === undefined ||
          e.geometry.coordinates === undefined
        ) {
          return acc
        }

        if (acc[e.properties.pattern]) {
          acc[e.properties.pattern].push(turf.point(point.geometry.coordinates))
        } else {
          acc[e.properties.pattern] = [turf.point(point.geometry.coordinates)]
        }

        return acc
      }, {})

      Object.keys(pointsByRegion).forEach((key) => {
        const coordinates = pointsByRegion[key]

        if (coordinates === undefined || coordinates.length < 3) {
          return
        }

        const points = turf.featureCollection(coordinates)

        const polygon = turf.buffer(turf.convex(points), 20, {
          units: 'meters',
        })

        const centerPoint = turf.centroid(polygon)

        const textRegionKey = 'regiontext' + key

        const textSource = this.map.getSource(textRegionKey)
        if (textSource) {
          textSource.setData(centerPoint)
        } else {
          this.map.addSource(textRegionKey, {
            type: 'geojson',
            data: centerPoint,
          })
        }

        this.map.addLayer({
          id: textRegionKey,
          type: 'symbol',
          source: textRegionKey,
          layout: {
            'text-field': key,
            'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
            'text-size': 16,
            'text-anchor': 'center',
          },
          paint: {
            'text-color': '#000000',
          },
        })

        const regionKey = 'region' + key

        const source = this.map.getSource(regionKey)
        if (source) {
          source.setData(polygon)
        } else {
          this.map.addSource(regionKey, {
            type: 'geojson',
            data: polygon,
          })
        }

        if (this.map.getLayer(regionKey)) {
          return
        }

        this.map.addLayer({
          id: regionKey + 'fill',
          type: 'fill',
          source: regionKey, // reference the data source
          layout: {},
          paint: {
            'fill-color': '#0080ff', // blue color fill
            'fill-opacity': 0.5,
          },
        })
      })
    },
    addLines() {
      if (this.linesGeoJson == null) {
        return
      }

      if (this.map == null) {
        return
      }

      if (this.lines === false) {
        return
      }

      const points = this.linesGeoJson

      const lines = points.features.map((e) =>
        turf.lineString(e.geometry.coordinates, {
          ...e.properties,
          diameter: +e.properties.diameter,
        })
      )

      const geojson = {
        features: lines,
        type: 'FeatureCollection',
      }

      const source = this.map.getSource('lines')
      if (source) {
        source.setData(geojson)
      } else {
        this.map.addSource('lines', {
          type: 'geojson',
          data: geojson,
        })
      }

      if (this.map.getLayer('lines')) {
        return
      }

      this.map.addLayer({
        id: 'lines',
        type: 'line',
        source: 'lines',
        paint: {
          'line-color': {
            property: 'diameter',
            type: 'exponential',
            stops: [...this.stopsLine],
          },
          'line-width': {
            property: 'diameter',
            type: 'interval',
            stops: this.stopsLine.map((e) => [e[0], e[0] / 20]),
          },
        },
      })
    },
    addReservatorios() {
      if (this.reservatoriosGeoJson == null) {
        return
      }

      if (this.map == null) {
        return
      }

      if (this.reservatorios === false) {
        return
      }

      this.map.loadImage(this.icones.reversatorio, (error, image) => {
        if (error) throw error
        this.map.addImage('reversatorio', image)

        const source = this.map.getSource('reservatorios')
        if (source) {
          source.setData(this.reservatoriosGeoJson)
        } else {
          this.map.addSource('reservatorios', {
            type: 'geojson',
            data: this.reservatoriosGeoJson,
          })
        }

        // Check if layer already exists
        if (this.map.getLayer('reservatorios')) {
          return
        }

        this.map.addLayer({
          id: 'reservatorios',
          type: 'symbol',
          source: 'reservatorios',
          layout: {
            'icon-image': 'reversatorio', // reference the image
            'icon-size': 0.1,
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
            'icon-optional': true,
            'icon-offset': [50, -150],
          },
        })
      })
    },
    addPoint() {
      if (this.map == null) {
        return
      }

      if (this.marker != null) {
        this.marker.remove()
      }

      if (this.point == null) {
        return
      }

      this.marker = new mapboxgl.Marker()
        .setLngLat([this.point.x, this.point.y])
        .addTo(this.map)
    },
    addTempLine() {
      if (this.map == null) {
        return
      }

      if (this.point == null) {
        const source = this.map.getSource('temp-line')
        if (source) {
          this.map.removeLayer('temp-line')
          this.map.removeSource('temp-line')
        }
        return
      }

      if (this.tempPointLine === null) {
        // Remove
        const source = this.map.getSource('temp-line')
        if (source) {
          this.map.removeLayer('temp-line')
          this.map.removeSource('temp-line')
        }
        return
      }

      const geoJsonData = {
        type: 'FeatureCollection',
        features: [
          turf.lineString(
            [[this.point.x, this.point.y], [...this.tempPointLine.coordinates]],
            {
              ...this.tempPointLine,
            }
          ),
        ],
      }

      const source = this.map.getSource('temp-line')
      if (source) {
        source.setData(geoJsonData)
      } else {
        this.map.addSource('temp-line', {
          type: 'geojson',
          data: geoJsonData,
        })
      }

      // Check if layer already exists
      if (this.map.getLayer('temp-line')) {
        return
      }

      // Must be default value
      this.map.addLayer({
        id: 'temp-line',
        type: 'line',
        source: 'temp-line',
        paint: {
          'line-color': '#ff00ff',
          'line-width': 5,
          'line-opacity': 0.7,
        },
      })
    },
    addPontos() {
      if (this.geojson == null) {
        return
      }

      if (this.map == null) {
        return
      }

      if (this.pontos === false) {
        return
      }

      const source = this.map.getSource('sensores')
      if (source) {
        source.setData(this.geojson)
      } else {
        this.map.addSource('sensores', {
          type: 'geojson',
          data: this.geojson,
        })
      }

      // Check if layer already exists
      if (this.map.getLayer('sensores-point')) {
        return
      }

      this.map.addLayer(
        {
          id: 'sensores-point',
          type: 'circle',
          source: 'sensores',
          minzoom: 0,
          paint: {
            // increase the radius of the circle as the zoom level and pressure value increases
            'circle-radius': {
              base: 10,
              stops: [
                [6, 5],
                [15, 10],
                [22, 20],
              ],
            },
            'circle-color': {
              property: 'pressure',
              type: 'exponential',
              stops: [...this.stops],
            },
            'circle-stroke-color': 'white',
            'circle-stroke-width': {
              stops: [
                [14, 0],
                [15, 0.7],
              ],
            },
            'circle-opacity': {
              stops: [
                [10, 1],
                [15, 0.7],
              ],
            },
            'circle-blur': {
              stops: [
                [10, 1],
                [15, 0],
              ],
            },
          },
        },
        'waterway-label'
      )
    },
    add3D() {
      if (this.geojson == null) {
        return
      }

      if (this.map == null) {
        return
      }

      if (this.threeD === false) {
        return
      }

      const points = this.geojson

      const circles = points.features.map((e) =>
        turf.circle(e, 20, {
          steps: 64,
          units: 'meters',
          properties: {
            ...e.properties,
            height: e.properties.pressure * 3,
          }, // optional: copy properties
        })
      )

      const geojson = {
        features: circles,
        type: 'FeatureCollection',
      }

      const source = this.map.getSource('polygon')
      if (source) {
        source.setData(geojson)
      } else {
        this.map.addSource('polygon', {
          type: 'geojson',
          data: geojson,
        })
      }

      this.map.addLayer({
        id: '3d',
        type: 'fill-extrusion',
        source: 'polygon',
        paint: {
          'fill-extrusion-color': {
            property: 'pressure',
            type: 'exponential',
            stops: [...this.stops],
          },
          'fill-extrusion-height': ['get', 'height'], // 'value' should be the height property in your data
          'fill-extrusion-base': 0,
          'fill-extrusion-opacity': 0.85,
        },
      })
    },
    async loadEquipamentos() {
      if (!this.geojson) {
        return
      }

      const response = await http.get(
        `local/${this.localId}/equipamentos`,
        {},
        false
      )
      if (!response || !response.equipamentos) return

      const equipamentosEpanet = response.equipamentos
        .filter((e) => e.sensores.some((x) => x.epanetId))
        .flatMap((e) =>
          e.sensores
            .filter((s) => s.epanetId)
            .map((s) => {
              return {
                ...e,
                sensor: s,
              }
            })
        )

      const geoJsonMap = {}

      this.geojson.features.forEach((e) => {
        geoJsonMap[e.properties.id] = e
      })

      this.equipamentosGeoJson = {
        type: 'FeatureCollection',
        features: equipamentosEpanet
          .filter((e) => geoJsonMap[e.sensor.epanetId])
          .map((e) => ({
            type: 'Feature',
            geometry: geoJsonMap[e.sensor.epanetId].geometry,
            properties: {
              id: e.id,
              nome: e.nome,
              epanetId: e.sensor.epanetId,
            },
          })),
      }

      this.addEquipamentos()
    },
    addEquipamentos() {
      if (!this.equipamentosGeoJson || !this.map) return

      const source = this.map.getSource('equipamentos')
      if (source) {
        source.setData(this.equipamentosGeoJson)
      } else {
        this.map.addSource('equipamentos', {
          type: 'geojson',
          data: this.equipamentosGeoJson,
        })
      }

      if (!this.map.getLayer('equipamentos-point')) {
        this.map.addLayer({
          id: 'equipamentos-point',
          type: 'circle',
          source: 'equipamentos',
          paint: {
            'circle-radius': {
              base: 8,
              stops: [
                [6, 6],
                [15, 12],
                [22, 18],
              ],
            },
            'circle-color': '#800080',
            'circle-stroke-color': '#ffffff',
            'circle-stroke-width': 2,
            'circle-opacity': 0.95,
          },
        })
      }

      if (!this.map.getLayer('equipamentos-label')) {
        this.map.addLayer({
          id: 'equipamentos-label',
          type: 'symbol',
          source: 'equipamentos',
          layout: {
            'text-field': ['get', 'nome'],
            'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
            'text-size': 11,
            'text-anchor': 'top',
            'text-offset': [0, 1.2],
            'text-allow-overlap': false,
          },
          paint: {
            'text-color': '#FF6600',
            'text-halo-color': '#ffffff',
            'text-halo-width': 1.5,
          },
        })
      }
    },
    loadMap() {
      mapboxgl.accessToken = environment.MAPBOXGL_ACCESSTOKEN

      const map = new mapboxgl.Map({
        container: 'mapContainer', // container ID
        style: this.styleMap, // style URL
        center: [-56.087104, -13.601664], // starting position [lng, lat]
        zoom: 9, // starting zoom
      })

      this.map = map

      map.on('load', async () => {
        map.resize()
        if (this.geojson != null) {
          map.flyTo({
            center: this.geojson.features[0].geometry.coordinates,
            zoom: 14,
          })
        }

        if (this.realTime) {
          this.connectEpanet()
        }
      })

      map.on('click', (e) => {
        this.$emit('clickmap', e)
      })

      map.on('mousemove', (e) => {
        this.$emit('mousemovemap', e)
      })

      map.on('style.load', async () => {
        this.addPontos()
        this.addTempLine()
        this.add3D()
        this.addRegions()
        this.addLines()
        this.addReservatorios()
        this.addEquipamentos()

        map.addSource('mapbox-dem', {
          type: 'raster-dem',
          url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
          tileSize: 512,
          maxzoom: 14,
        })
        // add the DEM source as a terrain layer with exaggerated height
        map.setTerrain({ source: 'mapbox-dem', exaggeration: 1 })
      })

      map.on('mousemove', 'sensores-point', () => {
        map.getCanvas().style.cursor = 'pointer'
      })

      map.on('mouseleave', 'sensores-point', () => {
        map.getCanvas().style.cursor = ''
      })

      map.on('click', 'sensores-point', (event) => {
        const idJuncao = event.features[0].properties.id
        delete event.features[0].properties.id
        const properties = Object.keys(event.features[0].properties)
          .sort()
          .reverse()
          .reduce((obj, key) => {
            obj[key] = event.features[0].properties[key]
            return obj
          }, {})
        this.$emit('update', {
          idJuncao,
          properties,
          x: event.features[0].geometry.coordinates[0],
          y: event.features[0].geometry.coordinates[1],
        })
      })

      map.on('mousemove', 'lines', () => {
        map.getCanvas().style.cursor = 'pointer'
      })

      map.on('mouseleave', 'lines', () => {
        map.getCanvas().style.cursor = ''
      })

      map.on('click', 'lines', (event) => {
        const idJuncao = event.features[0].properties.id
        delete event.features[0].properties.id
        const properties = Object.keys(event.features[0].properties)
          .sort()
          .reverse()
          .reduce((obj, key) => {
            obj[key] = event.features[0].properties[key]
            return obj
          }, {})
        this.$emit('update', {
          idJuncao,
          properties,
        })
      })

      map.on('mousemove', 'equipamentos-point', () => {
        map.getCanvas().style.cursor = 'pointer'
      })

      map.on('mouseleave', 'equipamentos-point', () => {
        map.getCanvas().style.cursor = ''
      })

      map.on('click', 'equipamentos-point', (event) => {
        event.preventDefault()
        const props = event.features[0].properties
        this.$emit('clickequipamento', {
          id: props.id,
          nome: props.nome,
          epanetId: props.epanetId || null,
        })
      })
    },
    getNodeById(nodeId) {
      if (!this.geojson || !nodeId) return null
      return (
        this.geojson.features.find((f) => f.properties.id === nodeId) || null
      )
    },
  },
}
</script>

<style scoped>
.fullHeight {
  height: 100%;
}

.legenda {
  padding: 0.2rem;
  position: absolute;
  z-index: 999;
  bottom: 0;
  background-color: #f5f5f5;
  border-radius: 0 0.5rem 0.5rem 0;
}

.posicao-right {
  padding: 0.2rem;
  position: absolute;
  z-index: 999;
  bottom: 0;
  right: 0;
}
</style>

<style>
.search-highlight-marker {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(255, 80, 80, 0.8);
  border: 2px solid white;
  box-shadow: 0 0 0 rgba(255, 80, 80, 0.6);
  animation: search-pulse 1s ease-out infinite;
}

@keyframes search-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(255, 80, 80, 0.6);
  }
  70% {
    box-shadow: 0 0 0 14px rgba(255, 80, 80, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 80, 80, 0);
  }
}
</style>
